<template>
  <div class="container">
    <InputFileDropBase
      :disabled="disabled"
      overlay-background-color="#d5d7f6"
      @files-dropped="emits('files', $event)"
      @items-dropped="emits('items', $event)"
      @keydown.enter="clickFileInput"
      @keydown.space="(event: Event) => clickFileInput(event, true)"
    >
      <template #content>
        <div class="content">
          <div
            :class="[
              'content__drop-zone__add-file__text',
              { 'content__drop-zone__add-file__text--disabled': disabled }
            ]"
          >
            <div class="top-text">
              <IconUpload :class="['icon', { 'icon--disabled': disabled }]" /><span>{{
                t('general__data__files-drop-here')
              }}</span>
            </div>
            <div class="content__drop-zone__add-file__text__buttons">
              <label :style="{ pointerEvents: disabled ? 'none' : 'auto' }">
                <input
                  ref="fileInput"
                  class="hidden-input"
                  type="file"
                  multiple
                  @change="onInputChange"
                />
                <ButtonOutline
                  :buttonText="t('general__data__files-add')"
                  size="small"
                  :disabled="disabled"
                >
                  <template #left>
                    <IconUploadFile />
                  </template>
                </ButtonOutline>
              </label>
              <span>{{ t('general__text__or').toLowerCase() }}</span>
              <label :style="{ pointerEvents: disabled ? 'none' : 'auto' }">
                <input
                  ref="dirInput"
                  class="hidden-input"
                  type="file"
                  webkitdirectory
                  directory
                  multiple
                  @change="onInputChange"
                />
                <ButtonOutline
                  :buttonText="t('general__data__directory-add')"
                  size="small"
                  :disabled="disabled"
                >
                  <template #left>
                    <IconUploadDirectory />
                  </template>
                </ButtonOutline>
              </label>
            </div>
          </div>
        </div>
      </template>
      <template #active>
        <span class="content__drop-zone__add-file__text">{{ t('general__data__file-drop') }}</span>
      </template>
      <template #disabled>
        <div>
          <div class="content__drop-zone__add-file__icon">
            <IconUpload />
          </div>
          <div class="content__drop-zone__add-file__text--disabled">
            <div>{{ t('general__text__files-drop-disabled') }}</div>
          </div>
        </div>
      </template>
    </InputFileDropBase>
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs } from 'vue'
import InputFileDropBase from './InputFileDropBase.vue'
import { useI18n } from 'vue-i18n'
import IconUpload from '@/design-system/assets/icons/IconUpload.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import IconUploadFile from '@/design-system/assets/icons/IconUploadFile.vue'
import IconUploadDirectory from '@/design-system/assets/icons/IconUploadDirectory.vue'

const props = defineProps({
  disabled: { type: Boolean, default: false }
})
const { disabled } = toRefs(props)
interface EMITS {
  (e: 'files', val: File[])
  (e: 'items', val: DataTransferItem[])
}
const emits = defineEmits<EMITS>()
const { t } = useI18n()
const dirInput = ref<HTMLInputElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

function clickFileInput(event?: Event, preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  if (fileInput.value) fileInput.value.click()
}

function onInputChange(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files) {
    emits('files', Array.from(files))
  }
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.container {
  border-radius: 12px;
  border: 1px dashed variables.$color-greyscale-secondary;
  overflow: hidden;
}

.container {
  // border-color: variables.$color-primary;
  // background-color: variables.$color-primary-light;
}

.content {
  padding: 48px 16px;

  &__disabled-drop-zone {
    align-items: center;
    background-color: variables.$color-greyscale-background;
    border-radius: 12px;
    border: 1px dashed variables.$color-greyscale-secondary;
    display: flex;
    justify-content: center;
    margin: 16px 0;
    &:hover {
      cursor: not-allowed;
    }
    &__error-text {
      @include font-mixins.desktop-regular-paragraph;
      color: variables.$color-error;
    }
  }
  &__drop-zone {
    text-align: center;
    align-items: center;
    display: flex;
    justify-content: center;
    > div {
      width: 100%;
    }
    .hidden-input {
      // Hide standard file input
      opacity: 0;
      z-index: -1;
      position: absolute;
    }
    &__add-file {
      &__text {
        @include font-mixins.desktop-bold-paragraph;
        width: 100%;
        color: variables.$color-greyscale-content;
        display: inline-block;
        vertical-align: top;
        &--disabled {
          @include font-mixins.desktop-bold-paragraph;
          color: variables.$color-greyscale-secondary;
        }

        .top-text {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 24px;
          align-items: center;
          flex-direction: row;
          vertical-align: middle;
          justify-content: center;
          .icon {
            color: variables.$color-primary;
            width: 24px;
            height: 24px;
            &--disabled {
              color: variables.$color-greyscale-secondary;
            }
            svg {
              width: 24px;
              height: 24px;
            }
          }
        }
        &__highlight {
          color: variables.$color-primary;
          cursor: pointer;
        }
        &__buttons {
          display: flex;
          flex-direction: row;
          gap: 16px;
          margin-top: 8px;
          align-items: center;
          justify-content: center;

          label {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
